import React, { useMemo } from 'react'
import dynamic from 'next/dynamic'
import PropTypes from 'prop-types'
import { responseToBlogPostProps } from './responseToBlogPostProps'
import ContentTemplate from '../../shared/ContentTemplate/index'
import H1 from '../../shared/Headings/H1'
import RichText from '../../shared/RichText'
import Accordion from '../../shared/FAQ/Accordion'
import TableOfContent from '../../shared/TableOfContent'
import styles from '../ArticleLayout/index.module.scss'
import RecommendedReads from '../../shared/RecommendedReads'
import { addHyperlinkTitle } from '../../../common/helpers'
import CustomJsonLd from '../../shared/CustomJsonLd'
import { APP_BASE_PATH } from '../../../common/constants'
import { toJsonLdDate } from 'src/common/utils'
import DataStrip from 'src/components/shared/DataStrip'
import Img from 'src/components/shared/Img'
import LazyLoad from 'react-lazyload'

const CustomerConceptLeadCTA = dynamic(() =>
  import('src/components/shared/CustomerConceptLeadCTA')
)

const Breadcrumb = dynamic(() => import('../../shared/Breadcrumb'))

const PureBlogPostLayout = ({
  breadcrumbs,
  image,
  title,
  author,
  modifiedDate,
  keyTakeaways,
  content,
  dataStrip,
  dataStripSource,
  faq,
  recommendedReads,
  pageSlug,
  tableOfContent,
  customPublishedDate,
  metaDescription,
  metaTitle,
  imageAsset,
  trustMarkersCombination,
  reviewer,
}) => {
  const jsonLdData = useMemo(() => {
    const jsonLdModifiedDate = modifiedDate
      ? toJsonLdDate(modifiedDate)
      : toJsonLdDate(customPublishedDate)

    const webPageData = {
      '@context': 'https://schema.org/',
      '@type': 'WebPage',
      '@id': `https://www.freedomdebtrelief.com/${pageSlug}#webpage`,
      name: metaTitle,
      url: `${APP_BASE_PATH()}${pageSlug}`,
      inLanguage: 'en-US',
      description: metaDescription,
      lastReviewed: jsonLdModifiedDate,
      dateCreated: customPublishedDate,
      dateModified: jsonLdModifiedDate,
    }

    if (author.fullName) {
      const sameAs = author.socialMedia.filter(
        (el) => el !== null && el !== undefined
      )
      const articleData = {
        '@context': 'https://schema.org/',
        '@type': 'Article',
        mainEntityOfPage: `https://www.freedomdebtrelief.com/${pageSlug}`,
        headline: title,
        description: metaDescription,
        keywords: keyTakeaways,
        datePublished: toJsonLdDate(customPublishedDate),
        dateModified: jsonLdModifiedDate,
        author: {
          '@type': 'Person',
          name: author.fullName,
          description: author.description,
          url: `${APP_BASE_PATH()}${author.url}`,
          sameAs: sameAs,
          image: {
            '@type': 'ImageObject',
            url: author.image,
            height: 200,
            width: 200,
          },
        },
        publisher: {
          '@type': 'Organization',
          name: 'Freedom Debt Relief',
          legalName: 'Freedom Debt Relief LLC',
          description:
            "Freedom Debt Relief is America's leader in credit card debt relief and debt consolidation to help clients lower their monthly payments without bankruptcy or loans.",
          url: 'https://www.freedomdebtrelief.com/#organization',
          sameAs: [
            'https://twitter.com/freedomdebt',
            'https://www.facebook.com/freedomdebtrelief/',
            'https://www.linkedin.com/company/freedom-debt-relief/',
            'https://www.youtube.com/freedomdebtrelief',
            'https://www.instagram.com/freedomdebtrelief/',
            'https://www.google.com/search?kgmid=/g/11stymh6d3',
            'https://www.crunchbase.com/organization/freedom-debt-relief-3bd9 ',
          ],
          logo: {
            '@type': 'ImageObject',
            url: 'https://www.freedomdebtrelief.com/next-assets/fdr-logo.webp',
            width: '160',
            height: '50',
          },
        },
      }
      if (imageAsset) {
        return Object.assign(articleData, {
          image: [
            {
              '@type': 'ImageObject',
              '@id':
                'https://www.freedomdebtrelief.com/debt-relief/#primaryimage',
              url: imageAsset?.url,
              width: imageAsset?.details?.image?.width,
              height: imageAsset?.details?.image?.height,
              caption: imageAsset?.title,
            },
          ],
        })
      } else return articleData
    } else return webPageData
  }, [
    customPublishedDate,
    metaDescription,
    metaTitle,
    pageSlug,
    author,
    imageAsset,
    title,
    keyTakeaways,
    modifiedDate,
  ])

  addHyperlinkTitle(content.content, title)
  const getVariations = () => {
    const values = ['molly', '', 'patti']
    const result = values.sort(() => Math.random() - 0.5)

    return result.slice(0, 2)
  }

  const randomCTAs = getVariations()

  return (
    <>
      <CustomJsonLd id={'blogLayout'} data={jsonLdData} />
      <div className="bg-gray-linear-xs pt-3.5 md:bg-gray-linear-md lg:pt-8">
        <div className="w-content gap-x-primary padding-x-primary grid grid-cols-4 md:grid-cols-8 lg:grid-cols-12">
          <div
            className="col-span-4 md:col-span-8 lg:col-span-10 lg:col-start-2"
            id="top-section"
          >
            <Breadcrumb items={breadcrumbs} />
            <H1
              text={title}
              id={title}
              className="mt-5 text-black-base lg:mt-9"
            />
          </div>
          <div className="col-span-4 md:col-span-8 lg:col-start-3">
            <div className="mb-4">
              <Img
                priority={true}
                src={image.path}
                customLoader={false}
                objectFit="cover"
                alt={image.label}
                imgClassName="rounded-xl"
                isBackgroundImage={false}
                className="h-[150px] md:h-[250px] lg:h-[350px]"
                styles={{ position: 'relative', height: '250px' }}
              />
            </div>
            <div className="mb-4 flex flex-col uppercase md:flex-row">
              <div className="flex">
                <pre className="block text-sm md:text-base">By </pre>
                <ContentTemplate.Author
                  className="border-black-base pr-3 text-sm md:border-r md:text-base"
                  fullName={author.fullName}
                  url={author.url}
                  showPrefixText={false}
                />
              </div>
              {reviewer && (
                <div className="flex pt-1 md:pt-0">
                  <pre className="block text-sm md:text-base">
                    {' '}
                    Reviewed By{' '}
                  </pre>
                  <ContentTemplate.Author
                    className="border-black-base pr-3 text-sm md:border-r md:text-base"
                    fullName={reviewer.fullName}
                    url={reviewer.url}
                    showPrefixText={false}
                  />
                </div>
              )}
              <div className="flex pt-1 md:pt-0">
                <pre className="block text-sm md:text-base"> Updated </pre>
                <ContentTemplate.ModifiedDate
                  ignoreHours
                  short
                  className="text-sm md:text-base"
                  date={modifiedDate || customPublishedDate}
                />
              </div>
            </div>
            {keyTakeaways.length ? (
              <ContentTemplate.KeyTakeaways list={keyTakeaways} />
            ) : null}
            {tableOfContent.length ? (
              <TableOfContent list={tableOfContent} />
            ) : null}
            <LazyLoad>
              <CustomerConceptLeadCTA
                trustMarkersCombination={trustMarkersCombination}
                className={'mt-8'}
                variation={randomCTAs[0]}
              />
            </LazyLoad>
            <div className="padding-t-primary">
              <RichText contentData={content} className={styles.richText} />
              <DataStrip dataStrip={dataStrip} source={dataStripSource} />
              <LazyLoad>
                <CustomerConceptLeadCTA
                  trustMarkersCombination={trustMarkersCombination}
                  className={'mt-8'}
                  variation={randomCTAs[1]}
                />
              </LazyLoad>
              <div className="margin-t-primary">
                <Accordion
                  showFirstDivider
                  questionClassName="lg:pb-8"
                  title="Frequently Asked Questions"
                  items={faq}
                />
              </div>
              {recommendedReads && recommendedReads.length ? (
                <RecommendedReads readList={recommendedReads} />
              ) : null}
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

PureBlogPostLayout.propTypes = {
  breadcrumbs: PropTypes.array.isRequired,
  image: PropTypes.object.isRequired,
  title: PropTypes.string.isRequired,
  author: PropTypes.object.isRequired,
  keyTakeaways: PropTypes.array.isRequired,
  content: PropTypes.object.isRequired,
  dataStrip: PropTypes.object,
  faq: PropTypes.array.isRequired,
  recommendedReads: PropTypes.array.isRequired,
  tableOfContent: PropTypes.array.isRequired,
  metaDescription: PropTypes.string.isRequired,
  metaTitle: PropTypes.string.isRequired,
  modifiedDate: PropTypes.string,
  updatedAt: PropTypes.string,
  imageAsset: PropTypes.object,
  pageSlug: PropTypes.string,
  dateCreated: PropTypes.string,
  variation: PropTypes.string,
}

const BlogPostLayout = responseToBlogPostProps(PureBlogPostLayout)

export default React.memo(BlogPostLayout)
